import classNames from "classnames";
import {
	AppIcon,
	DateString,
	Highlight,
	HighlightedText,
	ListItem,
	PipeSeparator,
	Typography
} from "client/Components";
import {
	EMPTY_ARRAY,
	isNilOrWhiteSpace,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { SubTitleSection } from "./SubTitleSection";
import { TimeZone } from "client/DateAndTime/timezones";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { AppColorViewModel } from "client/Api/Apps/common";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import appStyles from "./AppListItem.module.less";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";

export type IntegrationFlowListItemProps = {
	title: string | null;
	dateTime: string | Date | null;
	subTitle1: string | null;
	subTitle2: string | null;
	color: AppColorViewModel;
	icon: AppImageDisplayInfo;
	hitHighlights?: readonly HighlightItem[];
	onRemove?: () => void;
	timezone: TimeZone;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const IntegrationFlowListItem = (props: IntegrationFlowListItemProps): ReactElement => {
	const {
		title,
		dateTime,
		subTitle1,
		subTitle2,
		color,
		icon,
		hitHighlights = EMPTY_ARRAY,
		onRemove,
		timezone,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);
	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	return (
		<ListItem
			trackingId="integration-flow-item"
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div
						style={colorStyle}
						className={classNames(
							colorClass,
							appStyles.appIconWrapper
						)}
					>
						<div className={appStyles.appIcon}>
							<AppIcon
								value={icon}
								color="inherit" />
						</div>
					</div>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						isItalic={isNilOrWhiteSpace(title)}
						trackingId="title"
					>
						{isNotNilOrWhiteSpace(title)
							? (
								<HighlightedText
									text={title}
									highlight={highlights.get("title")}
								/>
							)
							: (
								localize("_NoTitle")
							)
						}
					</Typography>

				</ListItem.Title>
				<ListItem.SubTitle>
					{isNotNil(dateTime) && (
						<SubTitleSection
							trackingId="date-time"
							text={(
								<DateString
									format="RelativeDayWithTime"
									value={dateTime}
									timezone={timezone}
								/>
							)}
						/>
					)}
					<TextSeparator
						previousValue={dateTime}
						followingValues={[subTitle1, subTitle2]}
					/>
					<HighlightedText
						text={subTitle1}
						highlight={highlights.get("subTitle1")}
					/>
					<TextSeparator
						previousValue={subTitle1}
						followingValues={[subTitle2]}
					/>
					<HighlightedText
						text={subTitle2}
						highlight={highlights.get("subTitle2")}
					/>
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
IntegrationFlowListItem.displayName = "IntegrationFlowListItem";

type TextSeparatorProps = {
	previousValue: Date | string | nil;
	followingValues: readonly (Date | string | nil)[];
};

const TextSeparator = (props: TextSeparatorProps): ReactElement | null => {
	const {
		previousValue,
		followingValues
	} = props;

	const isVisible: boolean = Boolean(previousValue)
		&& followingValues.some(Boolean);

	return isVisible
		? (
			<PipeSeparator />
		)
		: null;
};
TextSeparator.displayName = "TextSeparator";